import { useState, useEffect } from 'react';
import { gameBridge } from '@/services/GameBridge';
import { HUD } from '@/components/game/HUD';
import { EncounterUI } from '@/components/game/EncounterUI';
import { JudgmentUI } from '@/components/game/JudgmentUI';
import { SettingsModal } from '@/components/game/SettingsModal';
import { CharacterSelectionModal } from '@/components/game/CharacterSelectionModal';
import { AchievementToast } from '@/components/game/AchievementToast';
import { AchievementsPanel } from '@/components/game/AchievementsPanel';

type GameScene = 'hub' | 'dungeon' | 'encounter' | 'judgment' | 'settings';

/**
 * Root React component that manages all game UI overlays
 * Renders on top of the Phaser canvas and responds to game events
 */
export function GameOverlay() {
  const [currentScene, setCurrentScene] = useState<GameScene>('hub');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [characterSelectionOpen, setCharacterSelectionOpen] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);

  // Subscribe to scene change events
  useEffect(() => {
    const unsubscribers = [
      gameBridge.subscribe('scene-changed', ({ scene }) => {
        setCurrentScene(scene);
      }),
      gameBridge.subscribe('settings-toggle', ({ open }) => {
        setSettingsOpen(open);
      }),
    ];

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, []);

  const handleSettingsClick = () => {
    setSettingsOpen(true);
    gameBridge.emit('settings-toggle', { open: true });
  };

  const handleAchievementsClick = () => {
    setAchievementsOpen(true);
  };

  const handleSettingsClose = (open: boolean) => {
    setSettingsOpen(open);
    if (!open) {
      gameBridge.emit('settings-toggle', { open: false });
    }
  };

  const handleCharacterSelect = () => {
    setSettingsOpen(false);
    setCharacterSelectionOpen(true);
  };

  return (
    <div className="fixed inset-0 pointer-events-none z-10">
      {/* HUD - always visible */}
      <div className="pointer-events-auto">
        <HUD onSettingsClick={handleSettingsClick} onAchievementsClick={handleAchievementsClick} />
      </div>

      {/* Encounter UI - visible during encounter scene */}
      <EncounterUI visible={currentScene === 'encounter'} />

      {/* Judgment UI - visible during judgment scene */}
      <JudgmentUI visible={currentScene === 'judgment'} />

      {/* Settings Modal - can be opened from any scene */}
      <div className="pointer-events-auto">
        <SettingsModal open={settingsOpen} onOpenChange={handleSettingsClose} onCharacterSelect={handleCharacterSelect} />
      </div>

      {/* Character Selection Modal */}
      <div className="pointer-events-auto">
      <CharacterSelectionModal open={characterSelectionOpen} onOpenChange={setCharacterSelectionOpen} />
    </div>

    {/* Achievement toast */}
    <AchievementToast />

    {/* Achievements Panel */}
    <AchievementsPanel open={achievementsOpen} onClose={() => setAchievementsOpen(false)} />
  </div>
);
}
